import {
  computed,
  ref,
  watchEffect,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useHost,
  type Ref,
  type ComputedRef,
} from "vue";
import { useSharedState } from "composables/useSharedState";
import { useVirtualList } from "composables/useVirtualList";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { createEventDispatcher } from "@/runtime/shared/events";
import { parseBoolAttr } from "@/runtime/shared/attrs";
import type { ChatProps, ChatMessage, ChatColor } from "./chat.types";
import { chatClasses } from "./chat.styles";
import { formatTime, formatDivider } from "./utils/timeFormat";
import { useChatMessages } from "./useChatMessages";

/* -------------------------------------------------------------------------- */
/*  Color → CSS variable map                                                   */
/* -------------------------------------------------------------------------- */

const CHAT_COLOR_VARS: Record<ChatColor, string> = {
  primary: "var(--color-primary)",
  secondary: "var(--color-secondary)",
  accent: "var(--color-accent)",
  error: "var(--color-error)",
  warning: "var(--color-warning)",
  info: "var(--color-info)",
  success: "var(--color-success)",
  neutral: "var(--color-neutral)",
  mono: "var(--color-foreground)",
};

/* -------------------------------------------------------------------------- */
/*  WeakMap registry — lets slot-mode e-chat-group find the scroll container   */
/* -------------------------------------------------------------------------- */

const chatScrollRegistry = new WeakMap<Element, HTMLElement>();

export const registerChatScroll = (chatHost: Element, scrollContainer: HTMLElement): void => {
  chatScrollRegistry.set(chatHost, scrollContainer);
};

export const getChatScroll = (chatHost: Element): HTMLElement | undefined => {
  return chatScrollRegistry.get(chatHost);
};

/* -------------------------------------------------------------------------- */
/*  WeakMap registry — lets slot-mode e-chat-bubble read its group's side      */
/* -------------------------------------------------------------------------- */

const chatGroupRegistry = new WeakMap<Element, "left" | "right">();

export const registerChatGroup = (groupHost: Element, side: "left" | "right"): void => {
  chatGroupRegistry.set(groupHost, side);
};

export const unregisterChatGroup = (groupHost: Element): void => {
  chatGroupRegistry.delete(groupHost);
};

export const getChatGroupSide = (groupHost: Element): "left" | "right" => {
  return chatGroupRegistry.get(groupHost) ?? "left";
};

/* -------------------------------------------------------------------------- */
/*  Options / Return types                                                     */
/* -------------------------------------------------------------------------- */

export interface UseChatOptions {
  props: Readonly<ChatProps>;
  scrollContainerRef: Ref<HTMLElement | null>;
  sentinelRef: Ref<HTMLElement | null>;
}

/* -------------------------------------------------------------------------- */
/*  Sub-builders                                                               */
/* -------------------------------------------------------------------------- */

type ChatState = ReturnType<typeof useSharedState<ChatProps>>;

/** Build derived reactive values from shared state. */
const buildChatDerived = (
  state: ChatState,
): {
  showAvatar: ComputedRef<boolean>;
  color: ComputedRef<ChatColor>;
  hasMore: ComputedRef<boolean>;
  loading: ComputedRef<boolean>;
} => {
  const showAvatar = computed(() => parseBoolAttr(state.showAvatar?.value as boolean | string | undefined, true));
  const color = computed<ChatColor>(() => {
    const val = state.color?.value as ChatColor | undefined;
    return val && val in CHAT_COLOR_VARS ? val : "primary";
  });
  const hasMore = computed(() => !!state.hasMore?.value);
  const loading = computed(() => !!state.loading?.value);
  return { showAvatar, color, hasMore, loading };
};

const BOTTOM_THRESHOLD = 50;

/** Create scroll-related state and helpers. */
const createChatScroll = (
  scrollContainerRef: Ref<HTMLElement | null>,
  groups: ReturnType<typeof useChatMessages>["groups"],
  scrollToKey: (key: string, behavior?: ScrollBehavior) => void,
) => {
  const isAtBottom = ref(true);
  const checkIsAtBottom = (): void => {
    const container = scrollContainerRef.value;
    if (!container) return;
    isAtBottom.value = container.scrollHeight - container.scrollTop - container.clientHeight < BOTTOM_THRESHOLD;
  };
  const scrollToBottom = (behavior: ScrollBehavior = "smooth"): void => {
    const container = scrollContainerRef.value;
    if (!container) return;
    requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior });
    });
  };
  const scrollToMessage = (id: string, behavior: ScrollBehavior = "smooth"): void => {
    const group = groups.value.find((g) => g.messages.some((m) => m.id === id));
    if (group) scrollToKey(group.id, behavior);
  };
  return { isAtBottom, checkIsAtBottom, scrollToBottom, scrollToMessage };
};

/** Build imperative message mutation API wrapping useChatMessages with scroll side-effects. */
const createChatMessageApi = (
  chatMessages: ReturnType<typeof useChatMessages>,
  scrollContainerRef: Ref<HTMLElement | null>,
  totalHeight: ReturnType<typeof useVirtualList>["totalHeight"],
  isAtBottom: Ref<boolean>,
  scrollToBottom: (behavior?: ScrollBehavior) => void,
  clearCache: () => void,
) => {
  const appendMessages = (msgs: ChatMessage | ChatMessage[]): void => {
    chatMessages.appendMessages(msgs);
    if (isAtBottom.value) nextTick(() => requestAnimationFrame(() => scrollToBottom()));
  };
  const prependMessages = (msgs: ChatMessage[]): void => {
    const prevTotal = totalHeight.value;
    chatMessages.prependMessages(msgs);
    nextTick(() => {
      requestAnimationFrame(() => {
        const container = scrollContainerRef.value;
        if (container) container.scrollTop += totalHeight.value - prevTotal;
      });
    });
  };
  const updateMessage = (id: string, updates: Partial<Pick<ChatMessage, "msg" | "status" | "streaming">>): void => {
    chatMessages.updateMessage(id, updates);
    if (isAtBottom.value) nextTick(() => requestAnimationFrame(() => scrollToBottom()));
  };
  const clearMessages = (): void => {
    chatMessages.clearMessages();
    clearCache();
  };
  return { appendMessages, prependMessages, updateMessage, clearMessages };
};

/** Wire up scroll listener, load-more sentinel, and bubble color CSS variable. */
const setupChatLifecycle = (
  host: Element | null,
  scrollContainerRef: Ref<HTMLElement | null>,
  sentinelRef: Ref<HTMLElement | null>,
  onScroll: () => void,
  scrollToBottom: (behavior?: ScrollBehavior) => void,
  color: ComputedRef<ChatColor>,
  hasMore: ComputedRef<boolean>,
  loading: ComputedRef<boolean>,
  dispatch: ReturnType<typeof createEventDispatcher>,
): void => {
  // CSS custom properties inherit across shadow boundaries so both array-mode shadow DOM
  // and slot-mode e-chat-bubble pick up the bubble color via this host assignment.
  watchEffect(() => {
    if (host) (host as HTMLElement).style.setProperty("--chat-user-color", CHAT_COLOR_VARS[color.value]);
  });
  let sentinelObserver: IntersectionObserver | null = null;
  onMounted(() => {
    const container = scrollContainerRef.value;
    if (container) {
      container.addEventListener("scroll", onScroll, { passive: true });
      scrollToBottom("auto");
      if (host) registerChatScroll(host, container);
    }
    const sentinel = sentinelRef.value;
    if (sentinel && container) {
      sentinelObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore.value && !loading.value) dispatch("chat:load-more");
        },
        { root: container },
      );
      sentinelObserver.observe(sentinel);
    }
  });
  onBeforeUnmount(() => {
    const container = scrollContainerRef.value;
    if (container) container.removeEventListener("scroll", onScroll);
    sentinelObserver?.disconnect();
  });
};

/** Group-based divider rule: first group OR day changed from previous group. */
const buildShouldShowDivider = (
  allGroups: ReturnType<typeof useChatMessages>["groups"]["value"],
  index: number,
): boolean => {
  if (index === 0) return true;
  if (index >= allGroups.length) return false;
  return allGroups[index].sentAt.slice(0, 10) !== allGroups[index - 1].sentAt.slice(0, 10);
};

/** Initialize the virtual list with fixed chat-item options. */
const createChatVirtualList = (
  groups: ReturnType<typeof useChatMessages>["groups"],
  scrollContainerRef: Ref<HTMLElement | null>,
) => {
  return useVirtualList({
    items: groups,
    scrollContainer: scrollContainerRef,
    itemKey: (group) => group.id,
    estimatedItemHeight: 72,
    overscan: 5,
  });
};

interface ChatReturnParts {
  props: Readonly<ChatProps>;
  classes: ComputedRef<ReturnType<typeof chatClasses>>;
  chatMessages: ReturnType<typeof useChatMessages>;
  virtual: ReturnType<typeof createChatVirtualList>;
  scroll: ReturnType<typeof createChatScroll>;
  api: ReturnType<typeof createChatMessageApi>;
  showAvatar: ComputedRef<boolean>;
  color: ComputedRef<ChatColor>;
  hasMore: ComputedRef<boolean>;
  loading: ComputedRef<boolean>;
}

const buildChatReturn = (parts: ChatReturnParts) => {
  const { props, classes, chatMessages, virtual, scroll, api, showAvatar, color, hasMore, loading } = parts;
  return {
    classes,
    isArrayMode: chatMessages.isArrayMode,
    groups: chatMessages.groups,
    virtualItems: virtual.virtualItems,
    totalHeight: virtual.totalHeight,
    topOffset: virtual.topOffset,
    measureItem: virtual.measureItem,
    appendMessage: (msg: ChatMessage) => api.appendMessages(msg),
    appendMessages: api.appendMessages,
    prependMessages: api.prependMessages,
    updateMessage: api.updateMessage,
    clearMessages: api.clearMessages,
    scrollToBottom: scroll.scrollToBottom,
    scrollToMessage: scroll.scrollToMessage,
    formatTime: (iso: string): string => formatTime(iso, props.timeFormat),
    shouldShowDivider: (index: number): boolean => buildShouldShowDivider(chatMessages.groups.value, index),
    formatDivider,
    loading,
    hasMore,
    showAvatar,
    color,
  };
};

/* -------------------------------------------------------------------------- */
/*  Composable                                                                 */
/* -------------------------------------------------------------------------- */

export const useChat = (options: UseChatOptions) => {
  const { props, scrollContainerRef, sentinelRef } = options;
  const host = useHost();
  const state = useSharedState(props);
  const dispatch = createEventDispatcher(host);

  const classes = computed(() => mergeClassProp(chatClasses({}), props.class));
  const { showAvatar, color, hasMore, loading } = buildChatDerived(state);

  const chatMessages = useChatMessages({ props });
  const virtual = createChatVirtualList(chatMessages.groups, scrollContainerRef);
  const scroll = createChatScroll(scrollContainerRef, chatMessages.groups, virtual.scrollToKey);
  const api = createChatMessageApi(
    chatMessages,
    scrollContainerRef,
    virtual.totalHeight,
    scroll.isAtBottom,
    scroll.scrollToBottom,
    virtual.clearCache,
  );

  setupChatLifecycle(
    host,
    scrollContainerRef,
    sentinelRef,
    scroll.checkIsAtBottom,
    scroll.scrollToBottom,
    color,
    hasMore,
    loading,
    dispatch,
  );

  return buildChatReturn({ props, classes, chatMessages, virtual, scroll, api, showAvatar, color, hasMore, loading });
};
